import React, { useState, useEffect } from 'react';
import { 
  Pill, Plus, Trash2, Edit2, AlertTriangle, RefreshCw, 
  Upload, Download, Search, Building2, Clock, CheckCircle,
  XCircle, AlertCircle, Loader2, FileText, Bell, Printer,
  ShieldAlert, ShieldCheck, Calendar
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { FormularyBadge, FormularyCheck, MedicationAuditModal, authFetch } from './MedicationExtras';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PatientMedicationsTab = ({ patientId, patientName }) => {
  const [medications, setMedications] = useState([]);
  const [allergies, setAllergies] = useState([]);
  const [refillReminders, setRefillReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showInteractionModal, setShowInteractionModal] = useState(false);
  const [showPharmacyModal, setShowPharmacyModal] = useState(false);
  const [showAllergyModal, setShowAllergyModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [interactions, setInteractions] = useState([]);
  const [checkingInteractions, setCheckingInteractions] = useState(false);
  const [syncingPharmacy, setSyncingPharmacy] = useState(false);
  const [editingMed, setEditingMed] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [documentText, setDocumentText] = useState('');
  const [showExtractModal, setShowExtractModal] = useState(false);
  const [pharmacyHistory, setPharmacyHistory] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [reportFormat, setReportFormat] = useState('comprehensive');
  const [formulary, setFormulary] = useState({});
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [sendingEmails, setSendingEmails] = useState(false);

  const fetchFormulary = async () => {
    const res = await authFetch(`/patients/${patientId}/formulary`);
    if (res.ok) setFormulary((await res.json()).results || {});
  };

  const sendRefillEmails = async () => {
    setSendingEmails(true);
    try {
      const res = await authFetch(`/patients/${patientId}/refill-reminders/send-now`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) toast.error(data.detail || 'Failed to send refill emails');
      else if (data.success) toast.success(data.message);
      else toast.error(data.message);
      fetchRefillReminders();
    } catch (error) {
      toast.error(`Failed to send refill emails: ${error.message}`);
    } finally {
      setSendingEmails(false);
    }
  };

  const getToken = () => {
    const ivoryUser = localStorage.getItem('ivory_user');
    if (ivoryUser) {
      try {
        return JSON.parse(ivoryUser).token;
      } catch {
        return localStorage.getItem('token');
      }
    }
    return localStorage.getItem('token');
  };

  useEffect(() => {
    if (patientId) {
      fetchMedications();
      fetchAllergies();
      fetchRefillReminders();
    }
  }, [patientId]);

  const fetchMedications = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/medications/patient/${patientId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setMedications(data.medications || []);
        fetchFormulary();
      }
    } catch (error) {
      console.error('Error fetching medications:', error);
      toast.error('Failed to load medications');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllergies = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/patients/${patientId}/allergies`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setAllergies(data.allergies || []);
      }
    } catch (error) {
      console.error('Error fetching allergies:', error);
    }
  };

  const fetchRefillReminders = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/patients/${patientId}/refill-reminders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setRefillReminders(data.reminders || []);
      }
    } catch (error) {
      console.error('Error fetching refill reminders:', error);
    }
  };

  const generateRefillReminders = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/patients/${patientId}/refill-reminders/generate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message || 'Refill reminders generated');
        fetchRefillReminders();
      }
    } catch (error) {
      console.error('Error generating reminders:', error);
      toast.error('Failed to generate refill reminders');
    }
  };

  const acknowledgeReminder = async (reminderId) => {
    try {
      const token = getToken();
      await fetch(`${API_URL}/api/refill-reminders/${reminderId}/acknowledge`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Reminder acknowledged');
      fetchRefillReminders();
    } catch (error) {
      console.error('Error acknowledging reminder:', error);
    }
  };

  const generateReport = async (format) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/patients/${patientId}/medication-report?format=${format}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setReportData(data.report);
        setReportFormat(format);
        setShowReportModal(true);
      }
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Failed to generate report');
    }
  };

  const checkAllInteractions = async () => {
    setCheckingInteractions(true);
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/medications/interactions/check-all?patient_id=${patientId}`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        setInteractions(data.interactions || []);
        setShowInteractionModal(true);
        if (data.interactions?.length > 0) {
          toast.warning(`Found ${data.interactions.length} drug interaction(s)`);
        } else {
          toast.success('No drug interactions found');
        }
      }
    } catch (error) {
      console.error('Error checking interactions:', error);
      toast.error('Failed to check drug interactions');
    } finally {
      setCheckingInteractions(false);
    }
  };

  const syncPharmacyData = async () => {
    setSyncingPharmacy(true);
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/pharmacy/sync/${patientId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message || 'Pharmacy data synced');
        fetchMedications();
      }
    } catch (error) {
      console.error('Error syncing pharmacy:', error);
      toast.error('Failed to sync pharmacy data');
    } finally {
      setSyncingPharmacy(false);
    }
  };

  const fetchPharmacyHistory = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/pharmacy/history/${patientId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setPharmacyHistory(data.history || []);
        setShowPharmacyModal(true);
      }
    } catch (error) {
      console.error('Error fetching pharmacy history:', error);
      toast.error('Failed to load pharmacy history');
    }
  };

  const extractMedications = async () => {
    if (!documentText.trim()) {
      toast.error('Please enter document text');
      return;
    }
    
    setExtracting(true);
    try {
      const token = getToken();
      const formData = new FormData();
      formData.append('document_text', documentText);
      
      const response = await fetch(`${API_URL}/api/medications/extract-from-document?patient_id=${patientId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`Extracted ${data.extractedCount} medications, saved ${data.savedCount} new`);
        setShowExtractModal(false);
        setDocumentText('');
        fetchMedications();
      } else {
        toast.error(data.detail || 'Failed to extract medications');
      }
    } catch (error) {
      console.error('Error extracting medications:', error);
      toast.error('Failed to extract medications');
    } finally {
      setExtracting(false);
    }
  };

  const deleteMedication = async (medId) => {
    if (!confirm('Delete this medication?')) return;
    
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/medications/${medId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Medication deleted');
        fetchMedications();
      }
    } catch (error) {
      console.error('Error deleting medication:', error);
      toast.error('Failed to delete medication');
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'major':
      case 'contraindicated':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'moderate':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'minor':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getSourceBadge = (source) => {
    switch (source) {
      case 'ai_extracted':
        return <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">AI Extracted</span>;
      case 'pharmacy_sync':
        return <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">Pharmacy Sync</span>;
      default:
        return <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">Manual</span>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#3498db]" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Allergies Alert Banner */}
      {allergies.length > 0 && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <ShieldAlert className="w-5 h-5 text-red-600" />
            <span className="font-semibold text-red-800">Patient Allergies ({allergies.length})</span>
            <Button size="sm" variant="ghost" onClick={() => setShowAllergyModal(true)} className="ml-auto text-red-600">
              Manage
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {allergies.map(a => (
              <span key={a.id} className={`px-2 py-1 rounded-full text-xs font-medium ${
                a.severity === 'Life-threatening' || a.severity === 'Severe' 
                  ? 'bg-red-200 text-red-800' 
                  : 'bg-yellow-200 text-yellow-800'
              }`}>
                {a.allergen} ({a.severity})
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Refill Reminders Alert */}
      {refillReminders.filter(r => r.status === 'pending').length > 0 && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Bell className="w-5 h-5 text-amber-600" />
            <span className="font-semibold text-amber-800">
              Refill Reminders ({refillReminders.filter(r => r.status === 'pending').length})
            </span>
          </div>
          <div className="space-y-1">
            {refillReminders.filter(r => r.status === 'pending').slice(0, 3).map(r => (
              <div key={r.id} className="flex items-center justify-between text-sm">
                <span className="text-amber-700">
                  <strong>{r.medicationName}</strong> - Due: {r.dueDate}
                </span>
                <Button size="sm" variant="ghost" onClick={() => acknowledgeReminder(r.id)} className="text-amber-600 h-6">
                  <CheckCircle className="w-3 h-3 mr-1" /> Done
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Header Actions */}
      <div className="flex flex-wrap items-center justify-between gap-3 p-4 bg-white rounded-lg border">
        <div className="flex items-center gap-2">
          <Pill className="w-5 h-5 text-[#3498db]" />
          <h3 className="font-semibold text-gray-800">Medications ({medications.length})</h3>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button 
            size="sm" 
            variant="outline"
            onClick={() => setShowAllergyModal(true)}
            className="text-red-600 border-red-300 hover:bg-red-50"
            data-testid="manage-allergies-btn"
          >
            <ShieldAlert className="w-4 h-4 mr-1" />
            Allergies ({allergies.length})
          </Button>
          <Button 
            size="sm" 
            variant="outline"
            onClick={() => generateReport('comprehensive')}
            className="text-green-600 border-green-300 hover:bg-green-50"
            data-testid="print-report-btn"
          >
            <Printer className="w-4 h-4 mr-1" />
            Print Report
          </Button>
          <Button 
            size="sm" 
            variant="outline"
            onClick={() => setShowAuditModal(true)}
            className="text-gray-800 border-gray-400 hover:bg-gray-100"
            data-testid="audit-log-btn"
          >
            <Clock className="w-4 h-4 mr-1" />
            Audit Log
          </Button>
          <Button 
            size="sm" 
            variant="outline"
            onClick={sendRefillEmails}
            disabled={sendingEmails}
            className="text-amber-700 border-amber-400 hover:bg-amber-50"
            data-testid="send-refill-emails-btn"
          >
            {sendingEmails ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Bell className="w-4 h-4 mr-1" />}
            Email Refill Reminders
          </Button>
          <Button 
            size="sm" 
            variant="outline"
            onClick={generateRefillReminders}
            data-testid="generate-reminders-btn"
          >
            <Bell className="w-4 h-4 mr-1" />
            Generate Refill Reminders
          </Button>
          <Button 
            size="sm" 
            onClick={() => setShowExtractModal(true)}
            className="bg-purple-600 hover:bg-purple-700"
            data-testid="extract-meds-btn"
          >
            <FileText className="w-4 h-4 mr-1" />
            Extract from Document
          </Button>
          <Button 
            size="sm" 
            variant="outline"
            onClick={syncPharmacyData}
            disabled={syncingPharmacy}
            data-testid="sync-pharmacy-btn"
          >
            {syncingPharmacy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Sync Pharmacy
          </Button>
          <Button 
            size="sm" 
            variant="outline"
            onClick={fetchPharmacyHistory}
            data-testid="pharmacy-history-btn"
          >
            <Building2 className="w-4 h-4 mr-1" />
            Pharmacy History
          </Button>
          <Button 
            size="sm" 
            variant="outline"
            onClick={checkAllInteractions}
            disabled={checkingInteractions || medications.length < 2}
            className="text-orange-600 border-orange-600 hover:bg-orange-50"
            data-testid="check-interactions-btn"
          >
            {checkingInteractions ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <AlertTriangle className="w-4 h-4 mr-1" />}
            Check Interactions
          </Button>
          <Button 
            size="sm" 
            onClick={() => setShowAddModal(true)}
            className="bg-[#3498db] hover:bg-[#2980b9]"
            data-testid="add-med-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Medication
          </Button>
        </div>
      </div>

      {/* Medications List */}
      {medications.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border">
          <Pill className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500">No medications recorded</p>
          <p className="text-sm text-gray-400 mt-1">Add medications manually, extract from documents, or sync from pharmacy</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {/* Routine Medications */}
          <div className="bg-white rounded-lg border p-4">
            <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Routine Medications
            </h4>
            <div className="space-y-2">
              {medications.filter(m => !m.isPRN).map(med => (
                <div key={med.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-testid={`med-${med.id}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-800">{med.name}</span>
                      {med.dosage && <span className="text-sm text-gray-600">{med.dosage}</span>}
                      {getSourceBadge(med.source)}
                      <FormularyBadge result={formulary[med.id]} testId={`formulary-badge-${med.id}`} />
                      {med.status === 'Active' ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : (
                        <XCircle className="w-4 h-4 text-gray-400" />
                      )}
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      {med.frequency} • {med.route} • Prescribed by {med.prescriber}
                      {med.usedFor && <span> • For: {med.usedFor}</span>}
                    </div>
                    {med.instructions && (
                      <div className="text-xs text-gray-400 mt-1 italic">{med.instructions}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setEditingMed(med)} data-testid={`med-edit-btn-${med.id}`}>
                      <Edit2 className="w-4 h-4 text-gray-500" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => deleteMedication(med.id)} data-testid={`med-delete-btn-${med.id}`}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
              {medications.filter(m => !m.isPRN).length === 0 && (
                <p className="text-sm text-gray-400 text-center py-2">No routine medications</p>
              )}
            </div>
          </div>

          {/* PRN Medications */}
          <div className="bg-white rounded-lg border p-4">
            <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              As Needed (PRN) Medications
            </h4>
            <div className="space-y-2">
              {medications.filter(m => m.isPRN).map(med => (
                <div key={med.id} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors" data-testid={`med-prn-${med.id}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-800">{med.name}</span>
                      {med.dosage && <span className="text-sm text-gray-600">{med.dosage}</span>}
                      <span className="px-2 py-0.5 text-xs bg-amber-200 text-amber-800 rounded-full">PRN</span>
                      {getSourceBadge(med.source)}
                      <FormularyBadge result={formulary[med.id]} testId={`formulary-badge-${med.id}`} />
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      {med.route} • {med.usedFor || 'As directed'}
                      {med.maxDose24hr && <span className="text-red-600"> • Max: {med.maxDose24hr}/24hr</span>}
                    </div>
                    {med.instructions && (
                      <div className="text-xs text-gray-400 mt-1 italic">{med.instructions}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setEditingMed(med)} data-testid={`med-edit-btn-${med.id}`}>
                      <Edit2 className="w-4 h-4 text-gray-500" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => deleteMedication(med.id)} data-testid={`med-delete-btn-${med.id}`}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
              {medications.filter(m => m.isPRN).length === 0 && (
                <p className="text-sm text-gray-400 text-center py-2">No PRN medications</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Drug Interactions Modal */}
      {showInteractionModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="bg-gradient-to-r from-orange-500 to-red-500 p-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Drug Interaction Check
                </h3>
                <button onClick={() => setShowInteractionModal(false)} className="text-white/80 hover:text-white">
                  <XCircle className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {interactions.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-3" />
                  <p className="text-lg font-medium text-gray-800">No Interactions Found</p>
                  <p className="text-sm text-gray-500">The current medications appear safe to use together</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {interactions.map((int, idx) => (
                    <div key={idx} className={`p-4 rounded-lg border ${getSeverityColor(int.severity)}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="w-5 h-5" />
                        <span className="font-semibold">{int.drug1} + {int.drug2}</span>
                        <span className="px-2 py-0.5 text-xs uppercase font-bold rounded bg-white/50">
                          {int.severity}
                        </span>
                      </div>
                      <p className="text-sm">{int.description}</p>
                      <p className="text-xs mt-2 opacity-70">Source: {int.source}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="border-t p-4 bg-gray-50">
              <Button variant="outline" onClick={() => setShowInteractionModal(false)}>Close</Button>
            </div>
          </div>
        </div>
      )}

      {/* Extract from Document Modal */}
      {showExtractModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full">
            <div className="bg-gradient-to-r from-purple-500 to-indigo-500 p-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Extract Medications from Document
                </h3>
                <button onClick={() => setShowExtractModal(false)} className="text-white/80 hover:text-white">
                  <XCircle className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-4">
              <p className="text-sm text-gray-600 mb-3">
                Paste discharge summaries, medication lists, or any document containing medication information. 
                AI will extract and add medications to the patient's profile.
              </p>
              <textarea
                value={documentText}
                onChange={(e) => setDocumentText(e.target.value)}
                placeholder="Paste document text here... 

Example:
Patient is currently taking:
- Lisinopril 10mg once daily for hypertension
- Metformin 500mg twice daily with meals for diabetes
- Tylenol 500mg as needed for pain (max 4 tablets/day)"
                className="w-full h-64 p-3 border rounded-lg text-sm resize-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                data-testid="extract-document-text"
              />
            </div>
            <div className="border-t p-4 bg-gray-50 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowExtractModal(false)}>Cancel</Button>
              <Button 
                onClick={extractMedications} 
                disabled={extracting || !documentText.trim()}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="extract-submit-btn"
              >
                {extracting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                Extract Medications
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Pharmacy History Modal */}
      {showPharmacyModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
            <div className="bg-gradient-to-r from-green-500 to-teal-500 p-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Pharmacy Fill History
                </h3>
                <button onClick={() => setShowPharmacyModal(false)} className="text-white/80 hover:text-white">
                  <XCircle className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {pharmacyHistory.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Building2 className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>No pharmacy history available</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {pharmacyHistory.map((rx, idx) => (
                    <div key={idx} className="p-3 bg-gray-50 rounded-lg border">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-gray-800">{rx.medication}</p>
                          <p className="text-sm text-gray-500">
                            Qty: {rx.quantity} • {rx.daysSupply} day supply • {rx.refillsRemaining} refills remaining
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {rx.pharmacy} • {rx.prescriber?.startsWith('Dr.') ? rx.prescriber : `Dr. ${rx.prescriber}`} • Rx# {rx.rxNumber}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-700">{rx.filledDate}</p>
                          <p className="text-xs text-gray-400">Filled</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="border-t p-4 bg-gray-50 flex justify-between">
              <Button variant="outline" onClick={syncPharmacyData} disabled={syncingPharmacy}>
                {syncingPharmacy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Sync to Patient Profile
              </Button>
              <Button variant="outline" onClick={() => setShowPharmacyModal(false)}>Close</Button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Medication Modal */}
      {(showAddModal || editingMed) && (
        <AddMedicationModal 
          patientId={patientId}
          editMed={editingMed}
          onClose={() => {
            setShowAddModal(false);
            setEditingMed(null);
          }}
          onSave={() => {
            setShowAddModal(false);
            setEditingMed(null);
            fetchMedications();
          }}
        />
      )}

      {/* Allergy Management Modal */}
      {showAuditModal && <MedicationAuditModal patientId={patientId} onClose={() => setShowAuditModal(false)} />}

      {showAllergyModal && (
        <AllergyModal 
          patientId={patientId}
          allergies={allergies}
          onClose={() => setShowAllergyModal(false)}
          onUpdate={fetchAllergies}
        />
      )}

      {/* Medication Report Modal */}
      {showReportModal && reportData && (
        <MedicationReportModal
          report={reportData}
          format={reportFormat}
          onClose={() => setShowReportModal(false)}
          onChangeFormat={(f) => generateReport(f)}
        />
      )}
    </div>
  );
};

// Add Medication Modal Component
const AddMedicationModal = ({ patientId, onClose, onSave, editMed = null }) => {
  const [formData, setFormData] = useState({
    name: editMed?.name || '',
    dosage: editMed?.dosage || '',
    frequency: editMed?.frequency || 'Once daily',
    route: editMed?.route || 'Oral',
    prescriber: editMed?.prescriber || '',
    startDate: editMed?.startDate || new Date().toISOString().split('T')[0],
    usedFor: editMed?.usedFor || '',
    instructions: editMed?.instructions || '',
    isPRN: editMed?.isPRN || false,
    maxDose24hr: editMed?.maxDose24hr || '',
    lastFilledDate: editMed?.lastFilledDate || '',
    nextRefillDate: editMed?.nextRefillDate || '',
    refillsRemaining: editMed?.refillsRemaining ?? ''
  });
  const [saving, setSaving] = useState(false);
  const [checkingNew, setCheckingNew] = useState(false);
  const [newDrugInteractions, setNewDrugInteractions] = useState([]);
  const [allergyWarnings, setAllergyWarnings] = useState([]);
  const [checkingAllergies, setCheckingAllergies] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const getToken = () => {
    const ivoryUser = localStorage.getItem('ivory_user');
    if (ivoryUser) {
      try {
        return JSON.parse(ivoryUser).token;
      } catch {
        return localStorage.getItem('token');
      }
    }
    return localStorage.getItem('token');
  };

  const checkAllergyForNewDrug = async () => {
    if (!formData.name.trim()) return;
    
    setCheckingAllergies(true);
    try {
      const token = getToken();
      const response = await fetch(
        `${API_URL}/api/patients/${patientId}/allergies/check-medication?medication_name=${encodeURIComponent(formData.name)}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      const data = await response.json();
      if (data.success) {
        setAllergyWarnings(data.warnings || []);
        if (data.warnings?.length > 0) {
          toast.error(`⚠️ ALLERGY WARNING: ${data.warnings[0].message}`);
        }
      }
    } catch (error) {
      console.error('Error checking allergies:', error);
    } finally {
      setCheckingAllergies(false);
    }
  };

  const checkInteractionsForNewDrug = async () => {
    if (!formData.name.trim()) return;
    
    setCheckingNew(true);
    try {
      const token = getToken();
      const response = await fetch(
        `${API_URL}/api/medications/interactions/check-all?patient_id=${patientId}&new_medication=${encodeURIComponent(formData.name)}`,
        {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      const data = await response.json();
      if (data.success) {
        setNewDrugInteractions(data.interactions || []);
      }
    } catch (error) {
      console.error('Error checking interactions:', error);
    } finally {
      setCheckingNew(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.prescriber) {
      toast.error('Please fill in required fields');
      return;
    }
    
    setSaving(true);
    try {
      const token = getToken();
      const isEdit = !!editMed?.id;
      const url = isEdit 
        ? `${API_URL}/api/medications/${editMed.id}`
        : `${API_URL}/api/medications`;
      
      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          lastFilledDate: formData.lastFilledDate || null,
          nextRefillDate: formData.nextRefillDate || null,
          refillsRemaining: formData.refillsRemaining === '' ? null : Number(formData.refillsRemaining),
          patientId
        })
      });
      const data = await response.json();
      if (data.success) {
        toast.success(isEdit ? 'Medication updated' : 'Medication added');
        onSave();
      } else {
        toast.error(data.detail || `Failed to ${isEdit ? 'update' : 'add'} medication`);
      }
    } catch (error) {
      console.error('Error saving medication:', error);
      toast.error('Failed to save medication');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
        <div className="bg-gradient-to-r from-[#3498db] to-[#2980b9] p-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <Pill className="w-5 h-5" />
              {editMed ? 'Edit Medication' : 'Add Medication'}
            </h3>
            <button onClick={onClose} className="text-white/80 hover:text-white">
              <XCircle className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Medication Name *</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  onBlur={() => { checkInteractionsForNewDrug(); checkAllergyForNewDrug(); }}
                  className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Lisinopril"
                  data-testid="med-name-input"
                />
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => { checkInteractionsForNewDrug(); checkAllergyForNewDrug(); }}
                  disabled={(checkingNew || checkingAllergies) || !formData.name.trim()}
                  title="Check interactions and allergies"
                >
                  {(checkingNew || checkingAllergies) ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-4 h-4" />}
                </Button>
              </div>
              <FormularyCheck patientId={patientId} drugName={formData.name} />
            </div>
            
            {/* Allergy Warnings */}
            {allergyWarnings.length > 0 && (
              <div className="col-span-2 p-3 bg-red-100 border-2 border-red-400 rounded-lg">
                <p className="text-sm font-bold text-red-800 flex items-center gap-1">
                  <ShieldAlert className="w-5 h-5" />
                  ⚠️ ALLERGY WARNING - Review Before Prescribing
                </p>
                {allergyWarnings.map((warn, idx) => (
                  <p key={idx} className="text-sm text-red-700 mt-1 font-medium">
                    • {warn.message}
                  </p>
                ))}
              </div>
            )}
            
            {newDrugInteractions.length > 0 && (
              <div className="col-span-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm font-medium text-red-800 flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" />
                  Potential Drug Interactions Detected
                </p>
                {newDrugInteractions.map((int, idx) => (
                  <p key={idx} className="text-xs text-red-600 mt-1">
                    • {int.drug1} + {int.drug2}: {int.description?.substring(0, 100)}...
                  </p>
                ))}
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Dosage</label>
              <input
                type="text"
                value={formData.dosage}
                onChange={(e) => setFormData({...formData, dosage: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., 10mg"
                data-testid="med-dosage-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
              <select
                value={formData.frequency}
                onChange={(e) => setFormData({...formData, frequency: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option>Once daily</option>
                <option>Twice daily</option>
                <option>Three times daily</option>
                <option>Four times daily</option>
                <option>Every 4 hours</option>
                <option>Every 6 hours</option>
                <option>Every 8 hours</option>
                <option>Every 12 hours</option>
                <option>At bedtime</option>
                <option>As needed</option>
                <option>Weekly</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Route</label>
              <select
                value={formData.route}
                onChange={(e) => setFormData({...formData, route: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option>Oral</option>
                <option>Topical</option>
                <option>Injectable</option>
                <option>Inhaled</option>
                <option>Sublingual</option>
                <option>Rectal</option>
                <option>Transdermal</option>
                <option>Ophthalmic</option>
                <option>Otic</option>
                <option>Nasal</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Prescriber *</label>
              <input
                type="text"
                value={formData.prescriber}
                onChange={(e) => setFormData({...formData, prescriber: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Dr. Smith"
                data-testid="med-prescriber-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Filled</label>
              <input
                type="date"
                value={formData.lastFilledDate}
                onChange={(e) => setFormData({...formData, lastFilledDate: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                data-testid="med-last-filled-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Next Refill Due</label>
              <input
                type="date"
                value={formData.nextRefillDate}
                onChange={(e) => setFormData({...formData, nextRefillDate: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                data-testid="med-next-refill-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Refills Remaining</label>
              <input
                type="number"
                min="0"
                value={formData.refillsRemaining}
                onChange={(e) => setFormData({...formData, refillsRemaining: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                data-testid="med-refills-remaining-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Used For</label>
              <input
                type="text"
                value={formData.usedFor}
                onChange={(e) => setFormData({...formData, usedFor: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., High blood pressure"
              />
            </div>
            <div className="col-span-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.isPRN}
                  onChange={(e) => setFormData({...formData, isPRN: e.target.checked})}
                  className="w-4 h-4 rounded border-gray-300"
                />
                <span className="text-sm text-gray-700">PRN (As Needed) Medication</span>
              </label>
            </div>
            {formData.isPRN && (
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Dose per 24hrs</label>
                <input
                  type="text"
                  value={formData.maxDose24hr}
                  onChange={(e) => setFormData({...formData, maxDose24hr: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., 4000mg or 8 tablets"
                />
              </div>
            )}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Special Instructions</label>
              <textarea
                value={formData.instructions}
                onChange={(e) => setFormData({...formData, instructions: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
                rows={2}
                placeholder="e.g., Take with food, avoid grapefruit"
              />
            </div>
          </div>
        </div>
        <div className="border-t p-4 bg-gray-50 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#3498db] hover:bg-[#2980b9]" data-testid="med-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : (editMed ? <Edit2 className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />)}
            {editMed ? 'Update Medication' : 'Add Medication'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Allergy Management Modal
const AllergyModal = ({ patientId, allergies, onClose, onUpdate }) => {
  const [newAllergy, setNewAllergy] = useState({
    allergen: '',
    allergenType: 'Drug',
    reaction: '',
    severity: 'Moderate'
  });
  const [saving, setSaving] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [docText, setDocText] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const getToken = () => {
    const ivoryUser = localStorage.getItem('ivory_user');
    if (ivoryUser) {
      try { return JSON.parse(ivoryUser).token; } catch { return localStorage.getItem('token'); }
    }
    return localStorage.getItem('token');
  };

  const addAllergy = async () => {
    if (!newAllergy.allergen.trim()) {
      toast.error('Please enter allergen name');
      return;
    }
    setSaving(true);
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/patients/${patientId}/allergies`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...newAllergy, patientId })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Allergy added');
        setNewAllergy({ allergen: '', allergenType: 'Drug', reaction: '', severity: 'Moderate' });
        onUpdate();
      } else {
        toast.error(data.detail || 'Failed to add allergy');
      }
    } catch (error) {
      toast.error('Failed to add allergy');
    } finally {
      setSaving(false);
    }
  };

  const deleteAllergy = async (allergyId) => {
    if (!confirm('Delete this allergy?')) return;
    try {
      const token = getToken();
      await fetch(`${API_URL}/api/patients/${patientId}/allergies/${allergyId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Allergy deleted');
      onUpdate();
    } catch (error) {
      toast.error('Failed to delete allergy');
    }
  };

  const extractAllergies = async () => {
    if (!docText.trim()) {
      toast.error('Please enter document text');
      return;
    }
    setExtracting(true);
    try {
      const token = getToken();
      const formData = new FormData();
      formData.append('document_text', docText);
      const response = await fetch(`${API_URL}/api/patients/${patientId}/allergies/extract-from-document`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`Extracted ${data.extractedCount} allergies, saved ${data.savedCount} new`);
        setDocText('');
        onUpdate();
      } else {
        toast.error(data.detail || 'Failed to extract allergies');
      }
    } catch (error) {
      toast.error('Failed to extract allergies');
    } finally {
      setExtracting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-red-500 to-pink-500 p-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <ShieldAlert className="w-5 h-5" />
              Patient Allergies
            </h3>
            <button onClick={onClose} className="text-white/80 hover:text-white">
              <XCircle className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="p-4 overflow-y-auto flex-1">
          {/* Add New Allergy */}
          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-700 mb-3">Add New Allergy</h4>
            <div className="grid grid-cols-2 gap-3">
              <input
                type="text"
                placeholder="Allergen (e.g., Penicillin)"
                data-testid="allergy-allergen-input"
                value={newAllergy.allergen}
                onChange={(e) => setNewAllergy({...newAllergy, allergen: e.target.value})}
                className="px-3 py-2 border rounded-lg text-sm"
              />
              <select
                value={newAllergy.allergenType}
                onChange={(e) => setNewAllergy({...newAllergy, allergenType: e.target.value})}
                data-testid="allergy-type-select"
                className="px-3 py-2 border rounded-lg text-sm"
              >
                <option>Drug</option>
                <option>Food</option>
                <option>Environmental</option>
                <option>Other</option>
              </select>
              <input
                type="text"
                placeholder="Reaction (e.g., Rash, hives)"
                data-testid="allergy-reaction-input"
                value={newAllergy.reaction}
                onChange={(e) => setNewAllergy({...newAllergy, reaction: e.target.value})}
                className="px-3 py-2 border rounded-lg text-sm"
              />
              <select
                value={newAllergy.severity}
                onChange={(e) => setNewAllergy({...newAllergy, severity: e.target.value})}
                data-testid="allergy-severity-select"
                className="px-3 py-2 border rounded-lg text-sm"
              >
                <option>Mild</option>
                <option>Moderate</option>
                <option>Severe</option>
                <option>Life-threatening</option>
              </select>
            </div>
            <div className="flex gap-2 mt-3">
              <Button onClick={addAllergy} disabled={saving} size="sm" data-testid="allergy-add-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                Add Allergy
              </Button>
            </div>
          </div>

          {/* Extract from Document */}
          <div className="mb-4 p-4 bg-purple-50 rounded-lg">
            <h4 className="font-medium text-purple-700 mb-2">Extract Allergies from Document</h4>
            <textarea
              value={docText}
              onChange={(e) => setDocText(e.target.value)}
              data-testid="allergy-extract-text"
              placeholder="Paste medical records, discharge summaries, or any document mentioning allergies..."
              className="w-full p-2 border rounded-lg text-sm h-20 resize-none"
            />
            <Button onClick={extractAllergies} disabled={extracting} size="sm" data-testid="allergy-extract-btn" className="mt-2 bg-purple-600 hover:bg-purple-700">
              {extracting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}
              Extract Allergies
            </Button>
          </div>

          {/* Allergies List */}
          <div className="space-y-2">
            {allergies.length === 0 ? (
              <p className="text-center text-gray-500 py-4">No allergies recorded</p>
            ) : (
              allergies.map(a => (
                <div key={a.id} data-testid={`allergy-item-${a.id}`} className={`flex items-center justify-between p-3 rounded-lg border ${
                  a.severity === 'Life-threatening' || a.severity === 'Severe' 
                    ? 'bg-red-50 border-red-200' 
                    : 'bg-yellow-50 border-yellow-200'
                }`}>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{a.allergen}</span>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        a.severity === 'Life-threatening' ? 'bg-red-200 text-red-800' :
                        a.severity === 'Severe' ? 'bg-orange-200 text-orange-800' :
                        a.severity === 'Moderate' ? 'bg-yellow-200 text-yellow-800' :
                        'bg-green-200 text-green-800'
                      }`}>{a.severity}</span>
                      <span className="px-2 py-0.5 text-xs bg-gray-200 text-gray-600 rounded-full">{a.allergenType}</span>
                      {a.source === 'ai_extracted' && <span className="px-2 py-0.5 text-xs bg-purple-200 text-purple-600 rounded-full">AI</span>}
                    </div>
                    {a.reaction && <p className="text-sm text-gray-600 mt-1">Reaction: {a.reaction}</p>}
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => deleteAllergy(a.id)} className="text-red-500">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))
            )}
          </div>
        </div>
        <div className="border-t p-4 bg-gray-50">
          <Button variant="outline" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
};

// Medication Report Modal
const MedicationReportModal = ({ report, format, onClose, onChangeFormat }) => {
  const printReport = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Medication Report - ${report.patient?.name}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto; }
            h1 { color: #333; border-bottom: 2px solid #3498db; padding-bottom: 10px; }
            h2 { color: #3498db; margin-top: 20px; }
            .header { display: flex; justify-content: space-between; margin-bottom: 20px; }
            .patient-info { background: #f5f5f5; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
            .allergies { background: #fee; padding: 15px; border-radius: 8px; margin-bottom: 20px; border-left: 4px solid #e74c3c; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background: #3498db; color: white; }
            tr:nth-child(even) { background: #f9f9f9; }
            .prn { background: #fff3cd; }
            .footer { margin-top: 30px; text-align: center; color: #666; font-size: 12px; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          <h1>Medication Report</h1>
          <div class="header">
            <div><strong>Generated:</strong> ${new Date(report.generatedAt).toLocaleString()}</div>
            <div><strong>Format:</strong> ${format.toUpperCase()}</div>
          </div>
          
          <div class="patient-info">
            <h3 style="margin-top:0">Patient Information</h3>
            <p><strong>Name:</strong> ${report.patient?.name || 'N/A'}</p>
            <p><strong>DOB:</strong> ${report.patient?.dateOfBirth || 'N/A'}</p>
            <p><strong>MRN:</strong> ${report.patient?.mrn || 'N/A'}</p>
            <p><strong>Address:</strong> ${report.patient?.address || 'N/A'}</p>
            <p><strong>Phone:</strong> ${report.patient?.phone || 'N/A'}</p>
            <p><strong>Physician:</strong> ${report.patient?.physician || 'N/A'}</p>
          </div>
          
          ${report.allergies?.length > 0 ? `
            <div class="allergies">
              <h3 style="margin-top:0;color:#c0392b">⚠️ ALLERGIES</h3>
              <ul>
                ${report.allergies.map(a => `<li><strong>${a.allergen}</strong> (${a.severity}) ${a.reaction ? `- ${a.reaction}` : ''}</li>`).join('')}
              </ul>
            </div>
          ` : ''}
          
          ${format === 'basic' && report.medications ? `
            <h2>Medications (${report.medications.length})</h2>
            <table>
              <tr><th>Medication</th><th>Dosage</th><th>Frequency</th><th>Route</th></tr>
              ${report.medications.map(m => `<tr><td>${m.name}</td><td>${m.dosage}</td><td>${m.frequency}</td><td>${m.route}</td></tr>`).join('')}
            </table>
          ` : ''}
          
          ${format === 'comprehensive' ? `
            <h2>Routine Medications (${report.routineMedications?.length || 0})</h2>
            <table>
              <tr><th>Medication</th><th>Dosage</th><th>Frequency</th><th>Route</th><th>Prescriber</th><th>For</th><th>Instructions</th></tr>
              ${(report.routineMedications || []).map(m => `<tr><td>${m.name}</td><td>${m.dosage}</td><td>${m.frequency}</td><td>${m.route}</td><td>${m.prescriber}</td><td>${m.usedFor || ''}</td><td>${m.instructions || ''}</td></tr>`).join('')}
            </table>
            
            <h2>PRN Medications (${report.prnMedications?.length || 0})</h2>
            <table class="prn">
              <tr><th>Medication</th><th>Dosage</th><th>Route</th><th>For</th><th>Max/24hr</th><th>Instructions</th></tr>
              ${(report.prnMedications || []).map(m => `<tr><td>${m.name}</td><td>${m.dosage}</td><td>${m.route}</td><td>${m.usedFor || ''}</td><td>${m.maxDose24hr || 'N/A'}</td><td>${m.instructions || ''}</td></tr>`).join('')}
            </table>
            
            ${report.pendingRefills?.length > 0 ? `
              <h2>Pending Refills</h2>
              <ul>${report.pendingRefills.map(r => `<li>${r.medication} - Due: ${r.dueDate}</li>`).join('')}</ul>
            ` : ''}
          ` : ''}
          
          ${format === 'mar' && report.marSchedule ? `
            <h2>Medication Administration Record (7-Day Schedule)</h2>
            <p><strong>Dates:</strong> ${report.marSchedule.dates.join(' | ')}</p>
            <table>
              <tr><th>Medication</th><th>Route</th><th>Scheduled Times</th><th>Instructions</th></tr>
              ${report.marSchedule.medications.map(m => `<tr><td>${m.name}</td><td>${m.route}</td><td>${m.scheduledTimes.join(', ')}</td><td>${m.instructions || ''}</td></tr>`).join('')}
            </table>
            
            <h2>PRN Medications</h2>
            <table class="prn">
              <tr><th>Medication</th><th>Indication</th><th>Max Dose</th></tr>
              ${(report.marSchedule.prnMedications || []).map(m => `<tr><td>${m.name}</td><td>${m.indication}</td><td>${m.maxDose}</td></tr>`).join('')}
            </table>
          ` : ''}
          
          <div class="footer">
            <p>This report was generated by IVORY Home Health Software</p>
            <p>Printed: ${new Date().toLocaleString()}</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-green-500 to-teal-500 p-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <Printer className="w-5 h-5" />
              Medication Report - {report.patient?.name}
            </h3>
            <button onClick={onClose} className="text-white/80 hover:text-white">
              <XCircle className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="p-4 border-b bg-gray-50">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Report Format:</span>
            <Button size="sm" variant={format === 'basic' ? 'default' : 'outline'} onClick={() => onChangeFormat('basic')} data-testid="report-format-basic">Basic</Button>
            <Button size="sm" variant={format === 'comprehensive' ? 'default' : 'outline'} onClick={() => onChangeFormat('comprehensive')} data-testid="report-format-comprehensive">Comprehensive</Button>
            <Button size="sm" variant={format === 'mar' ? 'default' : 'outline'} onClick={() => onChangeFormat('mar')} data-testid="report-format-mar">MAR (Clinical)</Button>
          </div>
        </div>
        <div className="p-4 overflow-y-auto flex-1">
          {/* Patient Info */}
          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-700 mb-2">Patient Information</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <p><span className="text-gray-500">Name:</span> <strong>{report.patient?.name}</strong></p>
              <p><span className="text-gray-500">DOB:</span> {report.patient?.dateOfBirth}</p>
              <p><span className="text-gray-500">MRN:</span> {report.patient?.mrn}</p>
              <p><span className="text-gray-500">Physician:</span> {report.patient?.physician}</p>
            </div>
          </div>

          {/* Allergies */}
          {report.allergies?.length > 0 && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <h4 className="font-medium text-red-700 mb-2">⚠️ Allergies</h4>
              <div className="flex flex-wrap gap-2">
                {report.allergies.map((a, idx) => (
                  <span key={idx} className="px-2 py-1 bg-red-200 text-red-800 rounded-full text-sm">
                    {a.allergen} ({a.severity})
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Medications */}
          <div className="mb-4">
            <h4 className="font-medium text-gray-700 mb-2">
              Active Medications ({report.activeMedications})
            </h4>
            
            {format === 'basic' && report.medications?.map((m, idx) => (
              <div key={idx} className="p-2 border-b text-sm">
                <strong>{m.name}</strong> {m.dosage} - {m.frequency} ({m.route})
              </div>
            ))}
            
            {format === 'comprehensive' && (
              <>
                <h5 className="text-sm font-medium text-gray-600 mt-3 mb-2">Routine Medications</h5>
                {report.routineMedications?.map((m, idx) => (
                  <div key={idx} className="p-3 bg-blue-50 rounded-lg mb-2 text-sm">
                    <div className="flex justify-between">
                      <strong>{m.name} {m.dosage}</strong>
                      <span className="text-gray-500">{m.frequency}</span>
                    </div>
                    <div className="text-gray-600 text-xs mt-1">
                      {m.route} • Prescribed by {m.prescriber} • {m.usedFor && `For: ${m.usedFor}`}
                    </div>
                    {m.instructions && <div className="text-xs text-gray-500 italic mt-1">{m.instructions}</div>}
                  </div>
                ))}
                
                <h5 className="text-sm font-medium text-amber-600 mt-4 mb-2">PRN Medications</h5>
                {report.prnMedications?.map((m, idx) => (
                  <div key={idx} className="p-3 bg-amber-50 rounded-lg mb-2 text-sm">
                    <div className="flex justify-between">
                      <strong>{m.name} {m.dosage}</strong>
                      <span className="text-amber-700">Max: {m.maxDose24hr || 'Per order'}</span>
                    </div>
                    <div className="text-gray-600 text-xs mt-1">
                      {m.route} • {m.usedFor}
                    </div>
                  </div>
                ))}
              </>
            )}
            
            {format === 'mar' && report.marSchedule && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border p-2 text-left">Medication</th>
                      <th className="border p-2">Route</th>
                      <th className="border p-2">Scheduled Times</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.marSchedule.medications?.map((m, idx) => (
                      <tr key={idx}>
                        <td className="border p-2 font-medium">{m.name}</td>
                        <td className="border p-2 text-center">{m.route}</td>
                        <td className="border p-2 text-center">{m.scheduledTimes?.join(', ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
        <div className="border-t p-4 bg-gray-50 flex justify-between">
          <Button onClick={printReport} className="bg-green-600 hover:bg-green-700" data-testid="report-print-btn">
            <Printer className="w-4 h-4 mr-2" />
            Print Report
          </Button>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
};

export default PatientMedicationsTab;
